/**
 * Language-preserving navigation utilities for React Router.
 *
 * Drop-in replacements for useNavigate / <Link> that automatically append
 * ?lang=ja (or whatever the active non-English locale is) so it is never
 * lost when the user moves between pages.
 *
 * Usage:
 *   import { useLangNavigate, LangLink } from "../../utils/langNavigation";
 *
 *   // Instead of:  const navigate = useNavigate(); navigate("/advisory");
 *   const navigate = useLangNavigate();
 *   navigate("/advisory");
 *
 *   // Instead of:  <Link to="/">Home</Link>
 *   <LangLink to="/">Home</LangLink>
 */

import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SUPPORTED_LANGUAGES = ["en", "ja"];

const normalizeLanguage = (lang) => {
  if (!lang) return "en";
  const base = String(lang).toLowerCase().split("-")[0];
  return SUPPORTED_LANGUAGES.includes(base) ? base : "en";
};

/** Append ?lang=<code> to a path when the language is not English. */
const addLangParam = (path, lang) => {
  const normalizedLang = normalizeLanguage(lang);
  if (normalizedLang === "en") return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}lang=${normalizedLang}`;
};

/**
 * Hook: works exactly like useNavigate() but preserves the current language
 * in the target URL.
 *
 * @returns {function} navigate(path, options?) – same signature as React Router
 */
export const useLangNavigate = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  return (path, options) => {
    const langPath = addLangParam(path, i18n.language);
    navigate(langPath, options);
  };
};

/**
 * Component: drop-in replacement for <Link> that appends the current language
 * query param automatically.
 *
 * @param {string} to   - destination path (e.g. "/", "/advisory")
 * @param props         - all other <Link> props are forwarded unchanged
 */
export const LangLink = ({ to, ...props }) => {
  const { i18n } = useTranslation();
  const langTo = addLangParam(to, i18n.language);
  return <Link to={langTo} {...props} />;
};
